import React from 'react';
import { BANDS } from './complianceBands';

/**
 * The headline figure of the Mandatory Compliance tab: what share of the staff
 * currently in scope have met the internal-mandatory target.
 *
 * The ring is the met / not-met split of the SAME scope, so one figure answers
 * "how are we doing" and "how many are left" at once. Everything
 * here is scope-aware — it redraws with the filters above it; that is the
 * point of the tab.
 *
 * Segments are direct-labelled in the legend below (count + share) and each
 * legend row is a filter toggle for that state.
 */
const RADIUS = 52;
const STROKE = 14;
const CIRC = 2 * Math.PI * RADIUS;
const GAP = 3; // surface gap between segments, in path units

const ComplianceDonut = ({ summary, target, activeBand, onBandToggle }) => {
  const { total, counts, rate, avgHours } = summary;

  // Segment geometry, laid out clockwise from 12 o'clock in fixed band order —
  // the order never depends on which band is largest.
  let offset = 0;
  const segments = BANDS.map((band) => {
    const value = counts[band.key] || 0;
    const length = total > 0 ? (value / total) * CIRC : 0;
    const seg = { band, value, length, offset };
    offset += length;
    return seg;
  }).filter((s) => s.value > 0);

  // A single band holding 100% would otherwise be drawn with a gap out of nowhere.
  const gapFor = (seg) => (segments.length > 1 ? Math.min(GAP, seg.length / 2) : 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-700">Overall Compliance</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Staff meeting the {target}-hour internal mandatory target, for the current filters
      </p>

      <div className="mt-4 flex justify-center">
        <div className="relative">
          <svg width="180" height="180" viewBox="0 0 120 120" role="img"
               aria-label={`${rate.toFixed(0)} percent of ${total} staff in scope have met the target`}>
            <circle cx="60" cy="60" r={RADIUS} fill="none" stroke="#e5e7eb" strokeWidth={STROKE} />
            <g transform="rotate(-90 60 60)">
              {segments.map((seg) => {
                const drawn = Math.max(0, seg.length - gapFor(seg));
                return (
                  <circle
                    key={seg.band.key}
                    cx="60" cy="60" r={RADIUS}
                    fill="none"
                    stroke={seg.band.hex}
                    strokeWidth={STROKE}
                    strokeDasharray={`${drawn} ${CIRC - drawn}`}
                    strokeDashoffset={-seg.offset}
                    opacity={activeBand && activeBand !== seg.band.key ? 0.25 : 1}
                  >
                    <title>{`${seg.band.label}: ${seg.value} of ${total}`}</title>
                  </circle>
                );
              })}
            </g>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-4xl font-bold text-gray-900 leading-none">
              {total > 0 ? `${rate.toFixed(0)}%` : '—'}
            </span>
            <span className="text-xs text-gray-500 mt-1">
              {counts.met} of {total} met
            </span>
          </div>
        </div>
      </div>

      {/* Legend — also the status filter. Colour is never the only cue: each row
          carries its label, the headcount and the share. */}
      <div className="mt-5 space-y-1">
        {BANDS.map((band, i) => {
          const value = counts[band.key] || 0;
          // Shares are rounded for display, and the last one takes whatever is
          // left — otherwise 62.5 and 37.5 both round up and the legend adds up
          // to 101%.
          const share = total === 0 ? 0
            : i < BANDS.length - 1
              ? Math.round((value / total) * 100)
              : 100 - BANDS.slice(0, -1).reduce((s, b) => s + Math.round(((counts[b.key] || 0) / total) * 100), 0);
          const active = activeBand === band.key;
          return (
            <button
              key={band.key}
              type="button"
              onClick={() => onBandToggle(band.key)}
              aria-pressed={active}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                active ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'
              }`}
            >
              <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${band.dot}`} />
              <span className="text-gray-700 flex-1 text-left">{band.label}</span>
              <span className="font-semibold text-gray-900 tabular-nums">{value}</span>
              <span className="text-gray-500 tabular-nums w-12 text-right">{share}%</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-3 text-center">
        <div>
          <p className="text-xs text-gray-500">Average Mandatory Hours</p>
          <p className="text-lg font-semibold text-gray-900 tabular-nums">{avgHours.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Target per Person</p>
          <p className="text-lg font-semibold text-gray-900 tabular-nums">{target}</p>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDonut;
